import React, { useState } from "react";
import { Share2, Check, Send } from "lucide-react";
import { useAuth } from "../lib/auth";
import { earnCoins } from "../lib/coins";
import { toast } from "sonner";

/**
 * Universal Share button. Uses the native Web Share API when available
 * (mobile + most modern browsers), otherwise copies the link.
 * For signed-in users we also show a "Send via DM" option that opens the
 * Messages page with a pre-filled message containing the file link.
 */
export default function ShareButton({ title, url, label = "Share", testId, small = false }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const targetUrl =
    url ||
    (typeof window !== "undefined" ? window.location.href : "");

  const onShare = async () => {
    const data = { title: title || document.title, url: targetUrl };
    // Coin award — server enforces +2 coins, daily cap 20. Failures silent.
    const awardCoins = () => {
      if (!user) return;
      earnCoins("share_dossier", targetUrl.slice(0, 80)).then((r) => {
        if (r?.awarded > 0) window.dispatchEvent(new Event("coins-changed"));
      });
    };
    // Web Share API — phones get native share sheet
    if (navigator.share) {
      try {
        await navigator.share(data);
        awardCoins();
        return;
      } catch {
        // user cancelled — fall through to copy
      }
    }
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(targetUrl);
        setCopied(true);
        toast.success("Link copied");
        setTimeout(() => setCopied(false), 1500);
        awardCoins();
        return;
      } catch {
        // fall through to legacy fallback
      }
    }
    // Legacy fallback — works inside iframes (e.g., preview environments)
    try {
      const ta = document.createElement("textarea");
      ta.value = targetUrl;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        setCopied(true);
        toast.success("Link copied");
        setTimeout(() => setCopied(false), 1500);
        awardCoins();
      } else {
        toast.error("Copy blocked — long-press the link instead");
      }
    } catch {
      toast.error("Couldn't copy — try again on the live site");
    }
  };

  const onSendDM = () => {
    // Prefill a session-storage hint that the messages page can read once,
    // but a simpler UX: route to messages, user picks recipient, then pastes.
    // We just put the URL on the clipboard and route them.
    navigator.clipboard?.writeText(`${title ? title + "\n" : ""}${targetUrl}`).catch(() => {});
    toast.success("Link copied — pick a friend to send it to");
    window.location.href = "/threads/messages";
  };

  const baseCls = small
    ? "inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase"
    : "inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase";

  return (
    <div className={small ? "inline-flex gap-1" : "inline-flex gap-2"}>
      <button
        type="button"
        onClick={onShare}
        data-testid={testId || "share-btn"}
        className={`${baseCls} ${
          small ? "px-2 py-1 border border-white/15" : "px-3 py-2 border border-white/15"
        } text-zinc-400 hover:text-[#60a5fa] hover:border-[#60a5fa]/60 transition-colors`}
        title="Share link"
      >
        {copied ? <Check size={small ? 11 : 13} /> : <Share2 size={small ? 11 : 13} />}
        {label}
      </button>
      {user && (
        <button
          type="button"
          onClick={onSendDM}
          data-testid="share-dm-btn"
          className={`${baseCls} ${
            small ? "px-2 py-1 border border-[#60a5fa]/30" : "px-3 py-2 border border-[#60a5fa]/30"
          } text-[#60a5fa] hover:bg-[#60a5fa]/10 transition-colors`}
          title="Send to a friend via DM"
        >
          <Send size={small ? 11 : 13} />
          DM
        </button>
      )}
    </div>
  );
}
